import React from 'react';
import { CircleCheck, CircleX, CircleEllipsis } from 'lucide-react'
import { Collapsible } from 'radix-ui';
import { MoldOrder } from '@/types/mold';


type ApprovalData = {
  id: number;
  stateInstanceId: number;
  stateInstanceName: string;
  approverType: 'role' | 'user';
  assignedApprover: string;
  actualApprover: string | null;
  status: 'approved' | 'rejected' | 'pending';
  at: string | null; // ISO date
  comment: string | null;
};

type ApprovalDataGroupedByState = {
  stateInstanceId: number;
  stateInstanceName: string;
  approvals: ApprovalData[];
}

// Group approval data by stateInstanceId
// Assuming the approval data is sorted by id and thus by stateInstanceId
const groupApprovalsByState = (data: ApprovalData[]): ApprovalDataGroupedByState[] => {
  return data.reduce((acc, curr) => {
    const existingGroup = acc.find(g => g.stateInstanceId === curr.stateInstanceId);
    if (existingGroup) {
      existingGroup.approvals.push(curr);
    } else {
      acc.push({
        stateInstanceId: curr.stateInstanceId,
        stateInstanceName: curr.stateInstanceName,
        approvals: [curr],
      });
    }
    return acc;
  }, [] as ApprovalDataGroupedByState[]);
}


// Convert moldOrder.stateInstances -> ApprovalData[]
const mapMoldOrderToApprovals = (moldOrder?: MoldOrder): ApprovalData[] => {
  if (!moldOrder || !moldOrder.stateInstances) return [];

  return moldOrder.stateInstances.flatMap((si) => {
    const name = si.status || (si as any).stateName || `state-${si.id}`;
    const slots = si.approverSlots || [];
    return slots.map((slot) => ({
      id: slot.id,
      stateInstanceId: si.id,
      stateInstanceName: name,
      approverType: slot.approverType,
      assignedApprover: slot.approverType === 'role'
        ? (slot.assignedRole?.role || 'ไม่ระบุ')
        : (slot.assignedUser?.fullname || slot.assignedUser?.username || 'ไม่ระบุ'),
      actualApprover: slot.actualApproverUser ? (slot.actualApproverUser.fullname || slot.actualApproverUser.username) : null,
      status: slot.status === 'approved' ? 'approved' : slot.status === 'rejected' ? 'rejected' : 'pending',
      at: slot.approvedAt || null,
      comment: slot.comments || null,
    }));
  });
}


type ApproverSlotCardProps = {
  data: ApprovalData;
};

const ApproverSlotCard: React.FC<ApproverSlotCardProps> = (props: ApproverSlotCardProps) => {
  const { data } = props;

  // Parameterized tailwind class names so callers can easily override if needed
  const classes = {
    // responsive: full width on small screens, constrained max width on larger screens
    container: 'w-full sm:max-w-md bg-white rounded-lg shadow-sm border border-gray-200',
    header: 'px-4 py-3 flex items-center justify-between',
    title: 'text-md font-medium text-gray-900',
    statusWrap: 'inline-flex items-center space-x-2 text-sm',
    icon: 'w-5 h-5 flex-shrink-0',
    statusText: 'text-sm',
    statusApproved: 'text-green-600',
    statusRejected: 'text-red-600',
    statusPending: 'text-yellow-500',
    // responsive: stack on mobile, two columns on small+; comment will span both columns
    body: 'px-4 py-3 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm',
    label: 'text-xs text-gray-500',
    value: 'text-sm text-gray-700 font-medium',
  };

  const renderStatusIcon = (status: ApprovalData['status']) => {
    const wrap = classes.statusWrap;
    const iconBase = classes.icon;

    switch (status) {
      case 'approved':
        return (
          <div className={wrap}>
            <CircleCheck className={`${iconBase} ${classes.statusApproved}`} />
            <span className={classes.statusText}>อนุมัติ</span>
          </div>
        );
      case 'rejected':
        return (
          <div className={wrap}>
            <CircleX className={`${iconBase} ${classes.statusRejected}`} />
            <span className={classes.statusText}>ไม่อนุมัติ</span>
          </div>
        );
      case 'pending':
      default:
        return (
          <div className={wrap}>
            <CircleEllipsis className={`${iconBase} ${classes.statusPending}`} />
            <span className={classes.statusText}>รอดำเนินการ</span>
          </div>
        );
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <div className={classes.title}>{data.assignedApprover}</div>
        <div className={classes.statusWrap} aria-hidden>
          {renderStatusIcon(data.status)}
        </div>
      </div>

      <div className={classes.body}>
        <div>
          <div className={classes.label}>ผู้ทำการอนุมัติ</div>
          <div className={classes.value}>{data.actualApprover ?? '-'}</div>
        </div>

        <div>
          <div className={classes.label}>เวลาอนุมัติ</div>
          <div className={classes.value}>{data.at ? new Date(data.at).toLocaleString() : '-'}</div>
        </div>

        <div>
          <div className={classes.label}>ข้อความ</div>
          <div className={classes.value}>{data.comment ?? '-'}</div>
        </div>
      </div>
    </div>
  );
};


type StateApprovalResultProps = {
  data: ApprovalDataGroupedByState[];
};

const StateApprovalResult: React.FC<StateApprovalResultProps> = (props: StateApprovalResultProps) => {
  const { data } = props;

  // Parameterized classes so callers can override appearance if needed
  const classes = {
  // outer wrapper to add separation between collapsible rows
  wrapper: 'space-y-3',
  // each root has border and subtle bg to be visually separable
  root: 'w-full border rounded-md bg-white shadow-sm',
  // trigger has clear contrast and rounded top
  triggerButton: 'w-full text-left px-4 py-3 bg-white hover:bg-gray-50 flex items-center justify-between rounded-t',
  title: 'font-medium text-gray-900',
  count: 'text-sm text-gray-500',
  // content uses a subtle background and border-top to separate from trigger
  content: 'mt-0 p-3 bg-gray-50 border-t border-gray-100 rounded-b',
  grid: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3',
  };

  if (!data || data.length === 0) return null;

  return (
    <div className={classes.wrapper}>
      {data.map(group => (
        // Make a collapsible group per state using radix-ui
        // The collapsible title should be state name
        <Collapsible.Root key={group.stateInstanceId} className={classes.root}>
          <Collapsible.Trigger asChild>
            <button
              type="button"
              className={classes.triggerButton}
            >
              <div className={classes.title}>{group.stateInstanceName}</div>
              {/* <div className={classes.count}>{group.approvals.length} approver{group.approvals.length !== 1 ? 's' : ''}</div> */}
            </button>
          </Collapsible.Trigger>

          <Collapsible.Content className={classes.content}>
            <div className={classes.grid}>
              {group.approvals.map(a => (
                <ApproverSlotCard key={a.id} data={a} />
              ))}
            </div>
          </Collapsible.Content>
        </Collapsible.Root>
      ))}
    </div>
  );
}

interface ApprovalHistoryTabProps {
  moldOrder: MoldOrder;
}

export const ApprovalHistoryTab: React.FC<ApprovalHistoryTabProps> = ({
  moldOrder,
}) => {
  // Map stateInstances -> flat approval entries, then group by state instance
  const approvals = mapMoldOrderToApprovals(moldOrder);
  const groupedData = groupApprovalsByState(approvals);

  // If there are no approvals, render a small empty state
  if (!groupedData || groupedData.length === 0) {
    return <div className="text-sm text-gray-500">ไม่มีประวัติการอนุมัติ</div>;
  }

  return (
    <StateApprovalResult data={groupedData} />
  );
}
